import { useEffect } from "react";
import { ArrowUpRight, MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";
import { useLang, BOOKING_URL, ADDRESS } from "../i18n";
import { Reveal } from "./Reveal";

const Footer = () => {
  const { t } = useLang();

  useEffect(() => {
    if (document.getElementById("iubenda-embed-script")) return;
    const s = document.createElement("script");
    s.id = "iubenda-embed-script";
    s.src = "https://cdn.iubenda.com/iubenda.js";
    document.body.appendChild(s);
  }, []);

  return (
    <footer data-testid="footer" className="bg-[#0F1A33] text-[#F9F8F6] pt-24 pb-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <img
              data-testid="footer-logo"
              src="/assets/logo-light.png"
              alt="King's Place Guest House"
              className="h-12 md:h-14 w-auto mb-6"
            />
            <p className="text-xs uppercase tracking-[0.25em] text-[#E8B54A]">{ADDRESS}</p>
            <a
              data-testid="footer-booking-cta"
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 group font-serif italic text-2xl md:text-3xl"
            >
              <span className="link-underline">{t.footer.bookCta}</span>
              <ArrowUpRight size={22} className="text-[#E8B54A] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </a>
          </div>

          <nav data-testid="footer-menu" className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] text-[#F9F8F6]/40 mb-5">{t.footer.exploreTitle}</p>
            <ul className="space-y-3">
              {[
                { href: "#camere", label: t.nav.rooms, id: "rooms" },
                { href: "#servizi", label: t.nav.amenities, id: "amenities" },
                { href: "#galleria", label: t.nav.gallery, id: "gallery" },
                { href: "#dintorni", label: t.nav.surroundings, id: "surroundings" },
                { href: "#recensioni", label: t.nav.reviews, id: "reviews" },
                { href: "#contatti", label: t.nav.contact, id: "contact" },
              ].map((l) => (
                <li key={l.id}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.id}`}
                    className="link-underline text-sm text-[#F9F8F6]/70 hover:text-[#E8B54A] transition-colors duration-300"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] text-[#F9F8F6]/40 mb-5">{t.footer.contactTitle}</p>
            <ul className="space-y-3 text-sm text-[#F9F8F6]/70">
              <li className="flex items-start gap-3">
                <MapPin size={15} className="text-[#E8B54A] mt-0.5 shrink-0" />
                <span>{ADDRESS}</span>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href="https://wa.me/393331991118"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 hover:text-[#E8B54A] transition-colors duration-300"
                >
                  <Phone size={15} className="text-[#E8B54A] mt-0.5 shrink-0" />
                  <span>+39 333 199 1118</span>
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-email"
                  href="mailto:info@kingsplace.it"
                  className="flex items-start gap-3 hover:text-[#E8B54A] transition-colors duration-300"
                >
                  <Mail size={15} className="text-[#E8B54A] mt-0.5 shrink-0" />
                  <span>info@kingsplace.it</span>
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-instagram"
                  href="https://www.instagram.com/kingsplace_roma"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 hover:text-[#E8B54A] transition-colors duration-300"
                >
                  <Instagram size={15} className="text-[#E8B54A] mt-0.5 shrink-0" />
                  <span>@kingsplace_roma</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={15} className="text-[#E8B54A] mt-0.5 shrink-0" />
                <span>
                  {t.contact.checkinLabel} {t.contact.checkin} · {t.contact.checkoutLabel} {t.contact.checkout}
                </span>
              </li>
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="font-serif leading-[0.85] tracking-tight text-[13.5vw] lg:text-[11vw] mt-16 select-none whitespace-nowrap text-[#F9F8F6]/95">
            King's <span className="italic text-[#E8B54A]">Place</span>
          </h2>
        </Reveal>

        <div className="mt-16 pt-8 border-t border-[#F9F8F6]/10 flex flex-wrap items-center justify-between gap-4 text-xs text-[#F9F8F6]/40 tracking-wide">
          <p data-testid="footer-host">
            {t.footer.rights} · {t.contact.hostLabel}: {t.contact.host}
          </p>
          <div data-testid="footer-legal-links" className="flex items-center gap-5">
            <a
              href="https://www.iubenda.com/privacy-policy/15450791"
              className="iubenda-white iubenda-noiframe iubenda-embed"
              title="Privacy Policy"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/15450791/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed"
              title="Cookie Policy"
            >
              Cookie Policy
            </a>
          </div>
          <p data-testid="footer-license">{t.footer.license}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
